import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, TextField, Button,
  Chip, Divider, MenuItem, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, LinearProgress, Switch, FormControlLabel,
} from '@mui/material';
import { Edit, People, Block, History, Share, Logout, Delete, Whatshot } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import AvatarUpload from '../../components/shared/AvatarUpload';
import AppSnackbar from '../../components/shared/AppSnackbar';
import { GlassCard } from '../../components/shared';
import { colors } from '../../theme/theme';
import { useTranslation } from 'react-i18next';

const INDUSTRIES = ['Tech', 'Design', 'Marketing', 'Finance', 'Education', 'Creative', 'Other'];

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { t } = useTranslation();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ bio: '', industry: '' });
  const [wallet, setWallet] = useState<any>(null);
  const [reputation, setReputation] = useState<any>(null);
  const [referral, setReferral] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' | 'info' | 'warning' });

  useEffect(() => {
    if (user) {
      setForm({ bio: user.bio || '', industry: user.industry || '' });
    }
    Promise.all([userService.getWallet(), userService.getReputation(), userService.getReferral()])
      .then(([w, r, ref]) => { setWallet(w); setReputation(r); setReferral(ref); })
      .catch(err => {
        // Non-blocking sidebar data — log for observability; profile still renders.
        console.warn('[ProfilePage] wallet/reputation/referral fetch failed', err);
      });
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await userService.updateProfile(form);
      await refreshUser();
      setEditing(false);
    } catch (err) {
      console.warn('[ProfilePage] profile save failed', err);
      setSnackbar({ open: true, message: 'Could not save profile — please try again', severity: 'error' });
    }
    finally { setSaving(false); }
  };

  const handleCopyReferral = () => {
    if (referral?.referral_link) {
      navigator.clipboard.writeText(referral.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!user) return null;

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* Avatar + Name */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <AvatarUpload
          currentUrl={user.avatar_url}
          firstName={user.first_name}
          lastName={user.last_name}
          size={100}
          onUploadSuccess={() => refreshUser()}
          onRemoveSuccess={() => refreshUser()}
        />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFFFFF' }}>{user.first_name} {user.last_name}</Typography>
        <Typography variant="body2" sx={{ color: colors.neutral[500] }}>{user.phone}</Typography>
        {user.industry && (
          <Chip
            label={user.industry}
            size="small"
            sx={{
              mt: 1,
              bgcolor: 'rgba(255,255,255,0.06)',
              color: '#FFFFFF',
              border: '1px solid rgba(255,255,255,0.08)',
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      {/* Reputation */}
      {reputation && (
        <GlassCard padding={3} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#FFFFFF' }}>{t('profile.reputation')}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ color: colors.neutral[700] }}>Score</Typography>
            <Typography sx={{ fontWeight: 700, color: reputation.score >= 70 ? colors.success.main : colors.warning.main }}>
              {reputation.score}/100
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={reputation.score}
            sx={{ height: 8, borderRadius: 4, mb: 1.5, bgcolor: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: reputation.score >= 70 ? colors.success.main : colors.warning.main } }} />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box><Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>{reputation.events_attended}</Typography><Typography variant="caption" sx={{ color: colors.neutral[500] }}>Events</Typography></Box>
            <Box><Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>{reputation.no_shows}</Typography><Typography variant="caption" sx={{ color: colors.neutral[500] }}>No-shows</Typography></Box>
            <Box><Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>{reputation.warnings}</Typography><Typography variant="caption" sx={{ color: colors.neutral[500] }}>Warnings</Typography></Box>
          </Box>
        </GlassCard>
      )}

      {/* Wallet */}
      {wallet && (
        <GlassCard padding={3} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>{t('profile.wallet')}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.primary.main }}>
              {wallet.balance} {wallet.currency}
            </Typography>
          </Box>
        </GlassCard>
      )}

      {/* Referral */}
      {referral?.referral_code && (
        <GlassCard padding={3} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#FFFFFF' }}>Invite Friends</Typography>
          <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 2 }}>
            Share your code. Both you and your friend get 2 AZN credit.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              label={referral.referral_code}
              sx={{
                fontFamily: 'monospace', fontWeight: 700, fontSize: '1rem', py: 2,
                bgcolor: 'rgba(232,135,92,0.12)',
                color: colors.primary.light,
                border: `1px solid ${colors.primary.main}33`,
              }}
            />
            <Button
              variant="outlined" size="small" startIcon={<Share />} onClick={handleCopyReferral}
              sx={{
                borderColor: 'rgba(255,255,255,0.12)',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { borderColor: 'rgba(255,255,255,0.25)', bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </Box>
        </GlassCard>
      )}

      {/* Bio Edit */}
      <GlassCard padding={3} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>{t('venues.about')}</Typography>
          <IconButton size="small" aria-label={editing ? 'Cancel editing about section' : 'Edit about section'} onClick={() => setEditing(!editing)} sx={{ color: colors.neutral[500], '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.04)' } }}><Edit /></IconButton>
        </Box>
        {editing ? (
          <>
            <TextField fullWidth label="Bio" value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              multiline rows={2} inputProps={{ maxLength: 280 }} sx={{ mb: 2 }} />
            <TextField fullWidth select label="Industry" value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} sx={{ mb: 2 }}>
              {INDUSTRIES.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
            </TextField>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ background: colors.primary.gradient, textTransform: 'none', fontWeight: 600 }}>{saving ? 'Saving...' : t('common.save')}</Button>
              <Button onClick={() => setEditing(false)} sx={{ color: colors.neutral[500], textTransform: 'none' }}>{t('common.cancel')}</Button>
            </Box>
          </>
        ) : (
          <Typography sx={{ color: colors.neutral[500] }}>{user.bio || 'No bio yet. Tap edit to add one.'}</Typography>
        )}
      </GlassCard>

      {/* Level & Streak */}
      <GlassCard padding={3} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
              {/* DS exemption: tier glyphs are brand whimsy in a clearly non-functional context (DESIGN_SYSTEM.md §Decorative emoji policy → Exemption #2). */}
              {user.user_level === 'explorer' ? '🌱' : user.user_level === 'regular' ? '⭐' : user.user_level === 'connector' ? '🤝' : '👑'}{' '}
              {(user.user_level || 'explorer').charAt(0).toUpperCase() + (user.user_level || 'explorer').slice(1)}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.neutral[500] }}>{user.events_attended} events attended</Typography>
          </Box>
          {(user.current_streak || 0) > 0 && (
            <Chip
              icon={<Whatshot sx={{ fontSize: 16 }} />}
              label={`${user.current_streak}-week streak`}
              sx={{
                bgcolor: colors.warning.subtle,
                color: colors.warning.main,
                fontWeight: 700,
                border: `1px solid ${colors.warning.main}40`,
                '& .MuiChip-icon': { color: colors.warning.main },
              }}
            />
          )}
        </Box>
        <LinearProgress variant="determinate"
          value={Math.min(100, ((user.events_attended || 0) / (user.user_level === 'explorer' ? 5 : user.user_level === 'regular' ? 11 : 25)) * 100)}
          sx={{ height: 8, borderRadius: 4, mb: 1, bgcolor: 'rgba(255,255,255,0.06)', '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: colors.primary.main } }} />
        <Typography variant="caption" sx={{ color: colors.neutral[500] }}>
          {user.user_level !== 'ambassador' ? `${Math.max(0, (user.user_level === 'explorer' ? 5 : user.user_level === 'regular' ? 11 : 25) - (user.events_attended || 0))} events to next level` : 'Max level reached.'}
        </Typography>
      </GlassCard>

      {/* Achievements */}
      {(user.achievements || []).length > 0 && (
        <GlassCard padding={3} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#FFFFFF' }}>Achievements ({(user.achievements || []).length}/10)</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(user.achievements || []).map((a: any) => (
              <Chip
                key={a.id}
                label={`${a.emoji} ${a.title}`}
                sx={{
                  bgcolor: 'rgba(251,191,36,0.12)',
                  color: '#FFFFFF',
                  border: `1px solid ${colors.accent.main}40`,
                  fontWeight: 600,
                }}
              />
            ))}
          </Box>
        </GlassCard>
      )}

      {/* Interests */}
      <GlassCard padding={3} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#FFFFFF' }}>{t('profile.interests')}</Typography>
        {(user.interests || []).length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {(user.interests || []).map((i: string) => (
              <Chip
                key={i}
                label={i}
                sx={{
                  bgcolor: 'rgba(232,135,92,0.12)',
                  color: colors.primary.light,
                  border: `1px solid ${colors.primary.main}33`,
                  fontWeight: 600,
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 1 }}>No interests added yet.</Typography>
        )}
        {user.looking_for && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: colors.neutral[700] }}>
            Looking for: {user.looking_for}
          </Typography>
        )}
        <Button size="small" sx={{ mt: 1, textTransform: 'none', color: colors.primary.main, fontWeight: 600, '&:hover': { bgcolor: 'rgba(232,135,92,0.08)' } }}
          onClick={() => navigate('/profile/interests')}>
          {(user.interests || []).length > 0 ? 'Edit interests' : 'Add interests'}
        </Button>
      </GlassCard>

      {/* Notification Preferences */}
      <GlassCard padding={3} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#FFFFFF' }}>{t('nav.notifications')}</Typography>
        <FormControlLabel control={<Switch defaultChecked />} label={t('profile.notifications_push')} sx={{ display: 'block', mb: 1, color: colors.neutral[700] }} />
        <FormControlLabel control={<Switch defaultChecked />} label={t('profile.notifications_email')} sx={{ display: 'block', color: colors.neutral[700] }} />
      </GlassCard>

      {/* Quick Links */}
      <GlassCard padding={0} sx={{ mb: 2 }}>
        {[
          { icon: <People sx={{ color: colors.primary.main }} />, label: t('profile.connections'), path: '/profile/connections' },
          { icon: <Block sx={{ color: colors.primary.main }} />, label: t('profile.blocked'), path: '/profile/blocked' },
          { icon: <History sx={{ color: colors.primary.main }} />, label: 'Event History', path: '/vouchers' },
        ].map((item, i) => (
          <Box key={i}>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 2, p: 2,
                cursor: 'pointer',
                color: '#FFFFFF',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
              onClick={() => navigate(item.path)}
            >
              {item.icon}
              <Typography sx={{ flex: 1, color: '#FFFFFF', fontWeight: 500 }}>{item.label}</Typography>
              <Typography sx={{ color: colors.neutral[500] }}>→</Typography>
            </Box>
            {i < 2 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />}
          </Box>
        ))}
      </GlassCard>

      {/* Logout + Delete */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button fullWidth variant="outlined" startIcon={<Logout />} onClick={() => { logout(); navigate('/login'); }}>
          {t('auth.logout')}
        </Button>
        <Button fullWidth variant="outlined" color="error" startIcon={<Delete />} onClick={() => setShowDelete(true)}>
          {t('profile.delete_account')}
        </Button>
      </Box>

      <Dialog open={showDelete} onClose={() => setShowDelete(false)}>
        <DialogTitle>Delete Account?</DialogTitle>
        <DialogContent><Typography>Your data will be anonymized after 30 days. This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDelete(false)}>Keep Account</Button>
          <Button color="error" variant="contained" onClick={async () => { await userService.deleteAccount(); logout(); navigate('/login'); }}>
            Delete Forever
          </Button>
        </DialogActions>
      </Dialog>

      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
    </Container>
  );
}
